import {
	canEditWorkingTimeTarget,
	canManageOtherUsersWorkingTimes,
	PERMISSION_KEYS_WITHOUT_ADMIN_SHORTCUT,
	supervisorMayCreateForDirectReports,
	workingTimesGeneratedFromProjectTimes,
} from "@timeghost/shared";
import { findWorkspaceUser } from "@timeghost/utils/helper";
import { projectMemberRole } from "@/lib/projects/project-membership";
import { canAccessTestingPage } from "@/lib/testing/can-access-testing";
import { DEFAULT_PERMISSION_GROUPS } from "./defaults";
import { hasPermission, isProjectManager } from "./has-permission";
import type { DashboardAccess, NavPermissions, PermissionSettingsKey, PermissionUser } from "./types";

export const isAdmin = (user: PermissionUser): boolean => hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, user);

export const isSupervisor = (user: PermissionUser): boolean =>
	!!user.workspace?.users?.find((workspaceUser) =>
		workspaceUser.supervisors?.find((supervisor) => supervisor.id === user.id)
	);

export const isSupervisorOfUser = (user: PermissionUser, subordinateId: string): boolean =>
	!!user.workspace?.users?.find(
		(workspaceUser) =>
			workspaceUser.id === subordinateId && workspaceUser.supervisors?.find((supervisor) => supervisor.id === user.id)
	);

/** Missing flags stay on. The direct-reports area is hidden only when every section is off. */
export const hasAnySupervisorView = (
	views: { readonly activity?: boolean; readonly rulebreaks?: boolean; readonly workinghours?: boolean } | undefined
): boolean => views?.activity !== false || views?.workinghours !== false || views?.rulebreaks !== false;

const isInPermissionGroup = (user: PermissionUser, groupId: string): boolean =>
	!!user.workspace?.groups?.find((group) => group.id === groupId)?.users?.some((member) => member.id === user.id);

export const hasUserRoleManager = (user: PermissionUser): boolean =>
	isInPermissionGroup(user, DEFAULT_PERMISSION_GROUPS.WorkspaceManager);

export const hasUserRoleControlling = (user: PermissionUser): boolean =>
	isInPermissionGroup(user, DEFAULT_PERMISSION_GROUPS.Controlling);

const skipAdminShortcut = new Set<string>(PERMISSION_KEYS_WITHOUT_ADMIN_SHORTCUT);

export const hasPermissionByKey = (user: PermissionUser, key: PermissionSettingsKey): boolean => {
	if (skipAdminShortcut.has(key)) {
		const refs = user.workspace?.permissionSettings?.[key];
		if (!refs?.length) {
			return false;
		}
		return refs.some((permission) => {
			if (permission.id === DEFAULT_PERMISSION_GROUPS.Everyone) {
				return true;
			}
			return !!user.workspace?.groups
				?.find((group) => group.id === permission.id)
				?.users?.find((groupUser) => groupUser.id === user.id);
		});
	}
	if (isAdmin(user)) {
		return true;
	}

	return !!user.workspace?.permissionSettings?.[key]?.find((permission) => hasPermission(permission.id, user));
};

/** Matches API `assertProjectWriteAllowed`: workspace admin, manage-all-projects, or manager of this project. */
export const canWriteProject = (
	user: PermissionUser | null | undefined,
	projectId: string,
	project?: {
		readonly groups?: readonly { readonly id: string; readonly removed?: boolean; readonly role?: string }[];
		readonly users?: readonly { readonly id: string; readonly removed?: boolean; readonly role?: string }[];
	}
): boolean => {
	if (!user?.id) {
		return false;
	}
	if (isAdmin(user) || hasPermissionByKey(user, "groupsCanManageAllProjects")) {
		return true;
	}
	if (isProjectManager(user.id, user.workspace, projectId)) {
		return true;
	}
	const userGroupIds = (user.workspace?.groups ?? [])
		.filter((group) => group.users?.some((member) => member.id === user.id))
		.map((group) => group.id);
	return projectMemberRole(user.id, project?.users ?? [], project?.groups ?? [], userGroupIds) === "manager";
};

const workingTimesManageAllIds = (user: PermissionUser): readonly string[] => {
	const stored = user.workspace?.permissionSettings?.groupsCanManageAllWorkingTimes;
	const refs =
		(Array.isArray(stored) ? stored : user.workspace?.permissionSettings?.groupsCanViewAllWorkingTimes) ?? [];
	return refs.map((group) => group.id);
};

const workingHoursTargetAllowed = (
	user: PermissionUser,
	targetUserId: string,
	ownKey: "groupsCanEditOwnWorkingTimes" | "groupsCanManuallyCreateWorkingTime"
): boolean =>
	canEditWorkingTimeTarget({
		canEditOwn: hasPermissionByKey(user, ownKey),
		isAdmin: isAdmin(user),
		isInGroup: (groupId) => hasPermission(groupId, user),
		isSelf: targetUserId === user.id,
		isSupervisor: isSupervisorOfUser(user, targetUserId),
		manageAllIds: workingTimesManageAllIds(user),
		settings: user.workspace?.settings,
	});

/** Update permission, plus own times, a supervised user, or manage-all. Opening Analytics does not include other people. */
export const canEditWorkingHoursOfUser = (user: PermissionUser | null | undefined, targetUserId: string): boolean =>
	!!user && workingHoursTargetAllowed(user, targetUserId, "groupsCanEditOwnWorkingTimes");

export const canCreateWorkingHoursOfUser = (user: PermissionUser | null | undefined, targetUserId: string): boolean => {
	if (!(user && workingHoursTargetAllowed(user, targetUserId, "groupsCanManuallyCreateWorkingTime"))) {
		return false;
	}
	if (targetUserId === user.id || !isSupervisorOfUser(user, targetUserId)) {
		return true;
	}
	return (
		supervisorMayCreateForDirectReports(user.workspace?.settings, "workinghours") ||
		canManageOtherUsersWorkingTimes({
			isAdmin: isAdmin(user),
			isInGroup: (groupId) => hasPermission(groupId, user),
			manageAllIds: workingTimesManageAllIds(user),
			settings: user.workspace?.settings,
		})
	);
};

export const canCreateAbsencesOfUser = (user: PermissionUser | null | undefined, targetUserId: string): boolean => {
	if (!user || isAdmin(user) || hasUserRoleManager(user)) {
		return !!user;
	}
	if (!hasPermissionByKey(user, "groupsCanCreateAbsences")) {
		return false;
	}
	return (
		targetUserId === user.id ||
		(isSupervisorOfUser(user, targetUserId) &&
			supervisorMayCreateForDirectReports(user.workspace?.settings, "absences"))
	);
};

/** Projects this user manages. Null means every project (admin). */
export const teamActivityManagedProjectIds = (user: PermissionUser): readonly string[] | null => {
	if (isAdmin(user)) {
		return null;
	}
	const workspace = user.workspace;
	const userGroupIds = (workspace?.groups ?? [])
		.filter((group) => group.users?.some((member) => member.id === user.id))
		.map((group) => group.id);
	const ids: string[] = [];
	for (const project of workspace?.projectPermissions ?? []) {
		if (projectMemberRole(user.id, project.users ?? [], project.groups ?? [], userGroupIds) !== "manager") {
			continue;
		}
		ids.push(project.projectId);
	}
	return ids;
};

/** Team activity lists other people only when this user may see their times. Null means every user. */
export const teamActivityScopeUserIds = (user: PermissionUser, tab: "project" | "comego"): readonly string[] | null => {
	if (isAdmin(user)) {
		return null;
	}
	const ids = new Set<string>([user.id]);
	if (tab === "comego") {
		for (const member of user.workspace?.users ?? []) {
			if (member.supervisors?.some((supervisor) => supervisor.id === user.id)) {
				ids.add(member.id);
			}
		}
	}
	return [...ids];
};

export const canSeeTeamActivityMostActive = (user: PermissionUser, tab: "project" | "comego"): boolean =>
	isAdmin(user) || (tab === "comego" && isSupervisor(user));

export const isTeamActivityRowVisible = (
	row: { readonly projectId: string | null; readonly userId: string },
	selfId: string | undefined,
	managedProjectIds: ReadonlySet<string> | null,
	userIds: ReadonlySet<string> | null
): boolean => {
	if (managedProjectIds) {
		return row.userId === selfId || (!!row.projectId && managedProjectIds.has(row.projectId));
	}
	return !(userIds && !userIds.has(row.userId));
};

/** Managers group, manage-times, or a project manager. */
export const canAssignTimeEntryUser = (user: PermissionUser | null | undefined): boolean =>
	!!user &&
	(hasUserRoleManager(user) ||
		hasPermissionByKey(user, "groupsCanManageTimes") ||
		isProjectManager(user.id, user.workspace));

export const canViewDashboard = (user: PermissionUser): false | DashboardAccess => {
	const workinghoursEnabled = user.workspace?.settings?.workinghoursEnabled ?? false;
	const projecthoursEnabled = user.workspace?.settings?.projecthoursEnabled ?? false;
	const timecontingentEnabled = !!user.workspace?.timecontingent?.enabled && workinghoursEnabled;

	const userIsAdmin = isAdmin(user);
	const userIsSupervisor = isSupervisor(user);
	const canSeeAllTimeAccounts = hasPermissionByKey(user, "groupsCanViewOtherTimeAccounts");
	const canSeeOwnContingent = hasPermissionByKey(user, "groupsCanViewOwnTimeAccount") || canSeeAllTimeAccounts;
	const canSeeSubordinatesContingent =
		!!user.workspace?.timecontingent?.superviseCanViewSubordinates && userIsSupervisor;

	const summaryPermission = projecthoursEnabled && hasPermissionByKey(user, "groupsCanViewProjectTimesInAnalytics");
	const detailedPermission =
		projecthoursEnabled &&
		hasPermissionByKey(user, "groupsCanViewProjectTimesInAnalytics") &&
		(hasPermissionByKey(user, "groupsCanEditOwnProjectTimes") ||
			isProjectManager(user.id, user.workspace) ||
			hasPermissionByKey(user, "groupsCanManageTimes"));
	const workingHoursPermission =
		workinghoursEnabled &&
		(hasPermissionByKey(user, "groupsCanViewWorkingTimesInAnalytics") ||
			hasPermissionByKey(user, "groupsCanViewAllWorkingTimes"));
	const timecontingentPermission = timecontingentEnabled && (canSeeOwnContingent || canSeeSubordinatesContingent);
	const canAccessDashboard =
		workingHoursPermission || timecontingentPermission || summaryPermission || detailedPermission;

	if (!canAccessDashboard) {
		return false;
	}

	let dashboardUrl: string | null = null;
	if (!workinghoursEnabled) {
		if (summaryPermission) {
			dashboardUrl = "/dashboard";
		} else if (detailedPermission) {
			dashboardUrl = "/dashboard/detailed";
		}
	} else if (workingHoursPermission) {
		dashboardUrl = "/dashboard/schedules";
	} else if (summaryPermission) {
		dashboardUrl = "/dashboard";
	} else if (detailedPermission) {
		dashboardUrl = "/dashboard/detailed";
	} else if (timecontingentPermission) {
		dashboardUrl = "/dashboard/contingent";
	}

	return {
		userIsAdmin,
		userIsSupervisor,
		projecthoursEnabled,
		summaryPermission,
		detailedPermission,
		workingHoursPermission: !!workingHoursPermission,
		timecontingentPermission: !!timecontingentPermission,
		dashboardUrl,
	};
};

const resolveSuperviseDefaultPath = (user: PermissionUser): string => {
	const views = user.workspace?.settings?.supervisorViews;
	if (views?.activity !== false) {
		return "/supervise/activity";
	}
	if (views?.workinghours) {
		return "/supervise/workinghours";
	}
	if (views?.rulebreaks) {
		return "/supervise/rulebreaks";
	}
	return "/supervise/activity";
};

export const resolveNavPermissions = (
	user: PermissionUser | null | undefined,
	options?: {
		readonly email?: string | null;
		readonly hostname?: string;
		readonly isProduction?: boolean;
	}
): NavPermissions => {
	const isProduction = options?.isProduction ?? import.meta.env.PROD;
	const empty: NavPermissions = {
		workinghoursEnabled: false,
		projecthoursEnabled: false,
		isAdmin: false,
		isSupervisor: false,
		hasRoleControlling: false,
		hasRoleManager: false,
		isProjectManager: false,
		canSeeTimer: false,
		canSeeFeed: false,
		canUseFeedEvents: false,
		canSeeInsightsSection: false,
		canSeeDashboard: false,
		dashboardUrl: null,
		canSeeTeamActivity: false,
		canSeeProfitability: false,
		canSeeSupervise: false,
		superviseDefaultPath: "/supervise/activity",
		canSeeManagementSection: false,
		canSeeProjects: false,
		canSeeClients: false,
		canSeeTags: false,
		canSeeWorkspaceAdminSection: false,
		canSeeSubscriptions: false,
		canSeeLogs: false,
		canSeeTesting: false,
		canSeeAutomation: !isProduction,
		groupsCanCreateProjects: false,
		groupsCanManageAllProjects: false,
		groupsCanManageClients: false,
		groupsCanManageTags: false,
		groupsCanSeeProjectsInNavigation: false,
	};

	if (!(user?.id && user.workspace)) {
		return empty;
	}

	const workinghoursEnabled = user.workspace.settings?.workinghoursEnabled ?? false;
	const projecthoursEnabled = user.workspace.settings?.projecthoursEnabled ?? false;
	const userIsAdmin = isAdmin(user);
	const userIsSupervisor = isSupervisor(user);
	const userIsProjectManager = isProjectManager(user.id, user.workspace);
	const roleControlling = hasUserRoleControlling(user);
	const roleManager = hasUserRoleManager(user);

	const groupsCanCreateProjects = hasPermissionByKey(user, "groupsCanCreateProjects");
	const groupsCanManageAllProjects = hasPermissionByKey(user, "groupsCanManageAllProjects");
	const groupsCanManageClients = hasPermissionByKey(user, "groupsCanManageClients");
	const groupsCanManageTags = hasPermissionByKey(user, "groupsCanManageTags");
	const groupsCanSeeProjectsInNavigation = hasPermissionByKey(user, "groupsCanSeeProjectsInNavigation");
	const groupsCanSeeTeamActivity = hasPermissionByKey(user, "groupsCanSeeTeamActivity");
	const groupsCanSeeTeamActivityWorkinghours = hasPermissionByKey(user, "groupsCanSeeTeamActivityWorkinghours");
	const groupsCanSeeProfitability = hasPermissionByKey(user, "groupsCanSeeProfitability");

	const dashboardAccess = canViewDashboard(user);
	const dashboardUrl = dashboardAccess === false ? null : dashboardAccess.dashboardUrl;
	const canSeeDashboard = !!dashboardUrl;
	const canSeeProfitability =
		!!user.workspace.settings?.profitabilityEnabled && groupsCanSeeProfitability && projecthoursEnabled;
	const canSeeSupervise =
		workinghoursEnabled && userIsSupervisor && hasAnySupervisorView(user.workspace.settings?.supervisorViews);
	const canSeeTeamActivity =
		(projecthoursEnabled && groupsCanSeeTeamActivity) || (workinghoursEnabled && groupsCanSeeTeamActivityWorkinghours);

	const canManuallyCreateProjectTime = hasPermissionByKey(user, "groupsCanManuallyCreateProjectTime");
	const canUseOwnProjectCapture =
		canManuallyCreateProjectTime ||
		hasPermissionByKey(user, "groupsCanStartStopProjectTime") ||
		hasPermissionByKey(user, "groupsCanEditOwnProjectTimes");
	const canUseOwnWorkCapture =
		hasPermissionByKey(user, "groupsCanManuallyCreateWorkingTime") ||
		hasPermissionByKey(user, "groupsCanStartStopWorkingTime");
	const canCreateAbsences = hasPermissionByKey(user, "groupsCanCreateAbsences");
	const generatedFromProject = workingTimesGeneratedFromProjectTimes(user.workspace.settings);
	const canSeeOwnWorkingHours =
		workinghoursEnabled &&
		(canCreateAbsences ||
			(!generatedFromProject && (canUseOwnWorkCapture || hasPermissionByKey(user, "groupsCanEditOwnWorkingTimes"))));

	const canSeeProjects =
		projecthoursEnabled &&
		(groupsCanCreateProjects || userIsProjectManager || groupsCanManageAllProjects || groupsCanSeeProjectsInNavigation);
	const canSeeClients = projecthoursEnabled && (groupsCanManageClients || roleControlling);
	const canSeeTags = projecthoursEnabled && (groupsCanManageTags || roleControlling);
	const canSeeManagementSection =
		projecthoursEnabled &&
		(groupsCanCreateProjects ||
			groupsCanManageClients ||
			groupsCanManageTags ||
			userIsProjectManager ||
			groupsCanManageAllProjects ||
			groupsCanSeeProjectsInNavigation);

	return {
		workinghoursEnabled,
		projecthoursEnabled,
		isAdmin: userIsAdmin,
		isSupervisor: userIsSupervisor,
		hasRoleControlling: roleControlling,
		hasRoleManager: roleManager,
		isProjectManager: userIsProjectManager,
		canSeeTimer: (projecthoursEnabled && canUseOwnProjectCapture) || canSeeOwnWorkingHours,
		canSeeFeed: projecthoursEnabled,
		canUseFeedEvents: canManuallyCreateProjectTime,
		canSeeInsightsSection:
			(canSeeDashboard || canSeeTeamActivity || canSeeSupervise || canSeeProfitability) &&
			(workinghoursEnabled || projecthoursEnabled),
		canSeeDashboard,
		dashboardUrl,
		canSeeTeamActivity,
		canSeeProfitability,
		canSeeSupervise,
		superviseDefaultPath: resolveSuperviseDefaultPath(user),
		canSeeManagementSection,
		canSeeProjects,
		canSeeClients,
		canSeeTags,
		canSeeWorkspaceAdminSection: userIsAdmin,
		canSeeSubscriptions: userIsAdmin,
		canSeeLogs: projecthoursEnabled && userIsAdmin && !isProduction,
		canSeeTesting: canAccessTestingPage({ email: options?.email, hostname: options?.hostname }),
		canSeeAutomation: !isProduction,
		groupsCanCreateProjects,
		groupsCanManageAllProjects,
		groupsCanManageClients,
		groupsCanManageTags,
		groupsCanSeeProjectsInNavigation,
	};
};

export const toPermissionUser = (value: unknown): PermissionUser | null => {
	if (!value || typeof value !== "object") {
		return null;
	}

	const candidate = value as {
		readonly email?: unknown;
		readonly id?: unknown;
		readonly workspace?: PermissionUser["workspace"];
	};

	if (typeof candidate.id !== "string" || candidate.id.length === 0) {
		return null;
	}

	const email = typeof candidate.email === "string" ? candidate.email : undefined;
	const member = findWorkspaceUser(candidate.workspace?.users, { id: candidate.id, email });

	return {
		id: member?.id ?? candidate.id,
		workspace: candidate.workspace ?? null,
	};
};
